import { useState, useEffect, useCallback, useRef } from 'react';
import { SERVICE_STATUSES } from '../../../constants';

const OSRM_BASE = 'https://router.project-osrm.org/route/v1/driving';

function decodeStepManeuver(type, modifier) {
  const map = {
    turn: 'Gira',
    'new name': 'Continúa en',
    depart: 'Inicia tu viaje hacia',
    arrive: 'Has llegado a tu destino',
    fork: 'Mantente a la',
    'end of road': 'Al final de la calle',
    'use lane': 'Usa el carril',
    continue: 'Continúa',
    'roundabout': 'Toma la rotonda',
    'rotary': 'Toma la glorieta',
    'roundabout turn': 'Toma la salida',
    'notification': 'Notificación',
    'merge': 'Incorpórate',
    'entrance': 'Entra',
    'exit': 'Sal',
    'ramp': 'Toma la rampa',
  };

  const modifiers = {
    left: 'a la izquierda',
    right: 'a la derecha',
    slight_left: 'levemente a la izquierda',
    slight_right: 'levemente a la derecha',
    sharp_left: 'bruscamente a la izquierda',
    sharp_right: 'bruscamente a la derecha',
    straight: 'recto',
    uturn: 'en U',
  };

  const action = map[type] || type;
  const dir = modifiers[modifier] || '';
  return dir ? `${action} ${dir}` : action;
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
}

function formatDistance(meters) {
  if (!meters || meters < 0) return '--';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

function findCurrentStepIndex(coordinates, driverPos, steps) {
  if (!driverPos || !coordinates || coordinates.length === 0) return 0;

  let minDist = Infinity;
  let closestIdx = 0;

  const searchEvery = Math.max(1, Math.floor(coordinates.length / 50));

  for (let i = 0; i < coordinates.length; i += searchEvery) {
    const [lat, lng] = coordinates[i];
    const d = Math.sqrt(
      Math.pow(lat - driverPos.lat, 2) +
      Math.pow(lng - driverPos.lng, 2)
    );
    if (d < minDist) {
      minDist = d;
      closestIdx = i;
    }
  }

  const fractionAlong = closestIdx / coordinates.length;
  const stepIndex = Math.floor(fractionAlong * steps.length);
  return Math.min(stepIndex, steps.length - 1);
}

export { decodeStepManeuver, formatDuration, formatDistance, findCurrentStepIndex };

export default function useRouteNavigation({ pickup, dropoff, driverPos, status, enabled = true }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const routeCache = useRef(new Map());

  const fetchRoute = useCallback(async () => {
    if (!pickup || !dropoff || !enabled) return;

    const start = [pickup.lng, pickup.lat];
    const end = [dropoff.lng, dropoff.lat];
    let waypoints = [];

    if (driverPos && (status === SERVICE_STATUSES.DRIVER_ASSIGNED || status === SERVICE_STATUSES.IN_PROGRESS)) {
      waypoints = [[driverPos.lng, driverPos.lat], ...(status === SERVICE_STATUSES.DRIVER_ASSIGNED ? [start] : []), end];
    } else {
      waypoints = [start, end];
    }

    const cacheKey = waypoints.map(w => w.join(',')).join(';');
    if (routeCache.current.has(cacheKey)) {
      const cached = routeCache.current.get(cacheKey);
      setRoute(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const coordsStr = waypoints.map(w => w.join(',')).join(';');
      const url = `${OSRM_BASE}/${coordsStr}?overview=full&geometries=geojson&steps=true&alternatives=false`;

      const res = await fetch(url);
      const data = await res.json();

      if (!data.routes || !data.routes[0]) {
        throw new Error('No se encontró ruta');
      }

      const osrmRoute = data.routes[0];
      const coords = osrmRoute.geometry.coordinates.map(c => [c[1], c[0]]);
      const steps = [];
      let stepIdx = 0;

      for (const leg of osrmRoute.legs) {
        for (const step of leg.steps) {
          steps.push({
            index: stepIdx++,
            instruction: step.maneuver.instruction || step.name,
            name: step.name,
            distance: step.distance,
            duration: step.duration,
            type: step.maneuver.type,
            modifier: step.maneuver.modifier,
            action: decodeStepManeuver(step.maneuver.type, step.maneuver.modifier),
            location: step.maneuver.location
              ? [step.maneuver.location[1], step.maneuver.location[0]]
              : null,
          });
        }
      }

      const totalDistance = osrmRoute.distance;
      const totalDuration = osrmRoute.duration;

      const routeData = {
        coordinates: coords,
        steps,
        totalDistance,
        totalDuration,
        legs: osrmRoute.legs,
      };

      routeCache.current.set(cacheKey, routeData);
      setTimeout(() => routeCache.current.delete(cacheKey), 300000);

      setRoute(routeData);
    } catch (err) {
      console.error('OSRM route error:', err);
      setError(err.message);

      const fallbackCoords = [
        [waypoints[0][1], waypoints[0][0]],
        [waypoints[waypoints.length - 1][1], waypoints[waypoints.length - 1][0]],
      ];
      const driverToPickup = driverPos && status === SERVICE_STATUSES.DRIVER_ASSIGNED;

      setRoute({
        coordinates: fallbackCoords,
        steps: [
          {
            index: 0,
            instruction: driverToPickup ? 'Dirígete al punto de recogida' : 'Dirígete al destino',
            name: '',
            distance: 0,
            duration: 0,
            type: 'depart',
            modifier: '',
            action: driverToPickup ? 'Dirígete al punto de recogida' : 'Dirígete al destino',
            location: null,
          },
        ],
        totalDistance: 0,
        totalDuration: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [pickup, dropoff, driverPos, status, enabled]);

  useEffect(() => {
    fetchRoute();
  }, [fetchRoute]);

  useEffect(() => {
    if (driverPos && route?.steps?.length > 0) {
      const idx = findCurrentStepIndex(route.coordinates, driverPos, route.steps);
      setCurrentStepIndex(idx);
    }
  }, [driverPos, route]);

  const remainingDistance = route
    ? route.steps.slice(currentStepIndex).reduce((sum, s) => sum + (s.distance || 0), 0)
    : 0;

  const remainingDuration = route
    ? route.steps.slice(currentStepIndex).reduce((sum, s) => sum + (s.duration || 0), 0)
    : 0;

  const currentStep = route?.steps?.[currentStepIndex] || null;
  const nextStep = route?.steps?.[currentStepIndex + 1] || null;

  const refetch = useCallback(() => {
    routeCache.current.clear();
    fetchRoute();
  }, [fetchRoute]);

  return {
    route,
    loading,
    error,
    currentStep,
    nextStep,
    currentStepIndex,
    totalSteps: route?.steps?.length || 0,
    totalDistance: route?.totalDistance || 0,
    totalDuration: route?.totalDuration || 0,
    remainingDistance,
    remainingDuration,
    refetch,
    formattedDuration: formatDuration,
    formattedDistance: formatDistance,
    formatDuration,
    formatDistance,
  };
}
